import { defineStore } from 'pinia'
import { ref, computed, onMounted } from 'vue'
import { TCString } from '@iabtechlabtcf/core'

import { getCookie } from './utils/helpers'

// Type definitions for Sourcepoint/TCF API
interface VendorGrant {
  vendorGrant: boolean
}

interface VendorConsents {
  grants: Record<string, VendorGrant>
}

interface ConsentConfig {
  isConsentWhitelistPage?: boolean,
  [key: string]: any
}

// TCF API callback type
type TCFAPICallback = (data: any, success: boolean) => void

export const useSourcepointStore = defineStore('sourcepoint', () => {
  // State
  const consentedVendors = ref<string[]>([])
  const consentConfig = ref<ConsentConfig>({})
  const consentAction = ref<boolean>(false)
  const consentString = ref<string>('')

  const consentHasBeenGiven = (): boolean => {
    if (!import.meta.env.SSR) {
      const consentString = getCookie('euconsent-v2')
      // just checking whether "euconsent-v2" is included in the cookie string is not enough
      // it's there even if all consents have been revoked ("ALLE EINWILLIGUNGEN WIDERRUFEN" button in the datenschutzmanager)
      // but with a (shorter) consent string, yet holding all the data; specifically `purposeConsents` and `vendorConsents`
      if (consentString) {
        try {
          setConsentString(consentString)
          const tcModel = TCString.decode(consentString)
          // if either of the maxIds is greater than 0, consent has been given. :empty-bucket-o-consents:
          // @ts-expect-error - maxId_ is private and only accessible within class 'Vector'
          return tcModel.purposeConsents.maxId_ > 0 || tcModel.vendorConsents.maxId_ > 0
        } catch (e) {
          console.error(e)
        }
      }
    }
    return false
  }

  // Getters
  const noScripts = computed((): boolean => {
    return !consentsExist.value || consentConfig.value.isConsentWhitelistPage === true
  })

  const consentsExist = computed((): boolean => {
    // don't check for consent on the server.
    if (import.meta.env.SSR) return false
    return consentAction.value || consentHasBeenGiven()
  })

  // Actions
  const setConsentConfig = (payload?: ConsentConfig): void => {
    consentConfig.value = payload || {}
  }

  const setConsentAction = (hasConsented: boolean): void => {
    consentAction.value = hasConsented
  }

  const setConsentString = (payload?: string): void => {
    consentString.value = payload || ''
  }

  const setVendorConsents = async (): Promise<void> => {
    if (!import.meta.env.SSR && window && (window as any).__tcfapi) {
      // https://documentation.sourcepoint.com/web-implementation/sourcepoint-gdpr-and-tcf-v2-support/__tcfapi-getcustomvendorconsents-api
      await new Promise<void>(resolve => {
        const tcfApi = (window as any).__tcfapi as (
          command: string,
          version: number,
          callback: TCFAPICallback,
          vendorIds?: string[],
          purposeIds?: string[],
          legitimateInterestPurposeIds?: string[]
        ) => void

        tcfApi('getCustomVendorConsents', 2, (vendorConsents: VendorConsents, _success: boolean) => {
          if (vendorConsents?.grants) {
            const grants = vendorConsents.grants
            const vendorIds = Object.keys(grants)
            consentedVendors.value = vendorIds.filter(key => grants[key]?.vendorGrant === true)
            localStorage.setItem('consentedVendors', JSON.stringify(consentedVendors.value))
          }
          resolve()
        })
      })
    }
  }

  // Function to load consentedVendors from local storage
  const loadConsentedVendors = (): void => {
    if (typeof window !== 'undefined') {
      const storedVendors = localStorage.getItem('consentedVendors')
      if (storedVendors) {
        try {
          consentedVendors.value = JSON.parse(storedVendors)
        } catch (e) {
          console.error('Failed to parse stored consent vendors:', e)
          consentedVendors.value = []
        }
      }
    }
  }

  const consentActionPerformed = async (): Promise<void> => {
    await setVendorConsents()
    setConsentAction(consentHasBeenGiven())
  }

  onMounted(() => {
    loadConsentedVendors()
  })

  return {
    consentConfig,
    consentString,
    noScripts,
    consentsExist,
    consentedVendors,
    setConsentConfig,
    setConsentString,
    consentActionPerformed
  }
})
